/**
 * "Classes Stay in the Same Venue": one room per class. The wizard sends `subjects[].roomId`
 * from `venueIdByClassName` (stored class name → venue id), which it learns from the bulk-import
 * response.
 *
 * On the AI server node-api owns those rooms (`venues_per_class: true` on the request) and
 * answers with `result.classVenues: [{id, name, classId, class}]`, every class room with its
 * STORED class name. Elsewhere the class name has to be read back out of the venue name
 * ("Venue 3 (Grade 1-A)").
 */

type Loose = Record<string, any>

/** Venue ids read from a bulk-import response. */
export type VenueIdsFromResponse = {
  /** Stored class name → venue id. */
  byClassName: Map<string, number>
  /** Venue name → venue id. */
  byVenueName: Map<string, number>
  /**
   * True when the map comes from `classVenues`, which lists every class room the school has:
   * it replaces the stored map (a deleted class's room is gone) instead of merging into it.
   */
  fromClassVenues: boolean
}

const positiveId = (value: unknown): number | null => {
  const id = Number(value)
  return Number.isFinite(id) && id > 0 ? id : null
}
const trim = (value: unknown) => String(value ?? '').trim()

/** Class name at the end of a generated venue name: "Venue 3 (Grade 1-A)" → "Grade 1-A". */
const classNameInVenueName = (name: string): string =>
  trim(/\(([^)]+)\)\s*$/.exec(name)?.[1])

/**
 * The venue ids a bulk-import response reports: from `classVenues` when the response has it
 * (`class` → `id`), else by parsing the class name out of the created / updated venues' names.
 * `byVenueName` is filled from both. Null when the response names no venue at all.
 */
export const venueIdsFromImportResponse = (res: unknown): VenueIdsFromResponse | null => {
  const r = (res ?? {}) as Loose
  const src: Loose = r?.result ?? r?.data ?? r ?? {}
  const created = src?.created ?? src?.result?.created ?? {}
  const updated = src?.updated ?? src?.result?.updated ?? {}
  const rawVenues: Loose[] = [
    ...(Array.isArray(created?.venues) ? created.venues : []),
    ...(Array.isArray(updated?.venues) ? updated.venues : []),
    ...(Array.isArray(src?.venues) ? src.venues : []),
  ]
  const classVenues = src?.classVenues ?? src?.result?.classVenues ?? r?.classVenues
  const fromClassVenues = Array.isArray(classVenues)

  const byClassName = new Map<string, number>()
  const byVenueName = new Map<string, number>()
  const addVenueName = (name: string, id: number) => {
    if (name && !byVenueName.has(name)) byVenueName.set(name, id)
  }
  if (fromClassVenues) {
    ;(classVenues as Loose[]).forEach((venue) => {
      const id = positiveId(venue?.id)
      if (!id) return
      addVenueName(trim(venue?.name), id)
      const className = trim(venue?.class)
      if (className && !byClassName.has(className)) byClassName.set(className, id)
    })
  }
  rawVenues.forEach((venue) => {
    const id = positiveId(venue?.id)
    const name = trim(venue?.name)
    if (!id || !name) return
    addVenueName(name, id)
    // Only without `classVenues` (non-AI server): the name is the class name's one source.
    if (fromClassVenues) return
    const className = classNameInVenueName(name)
    if (className && !byClassName.has(className)) byClassName.set(className, id)
  })
  if (!fromClassVenues && byClassName.size === 0 && byVenueName.size === 0) return null
  return {byClassName, byVenueName, fromClassVenues}
}

/**
 * The stored `venueIdByClassName` after a response: replaced by `classVenues`, else the parsed
 * names merged over the previous map. Unchanged (`previous`) when the response named no class.
 */
export const nextVenueIdByClassName = (
  previous: Record<string, number> | null | undefined,
  ids: VenueIdsFromResponse
): Record<string, number> | null | undefined => {
  if (ids.fromClassVenues) return Object.fromEntries(ids.byClassName)
  if (ids.byClassName.size === 0) return previous
  return {...(previous ?? {}), ...Object.fromEntries(ids.byClassName)}
}
